import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, X, Share, Smartphone, MoreVertical, MonitorDown, CheckCircle2 } from "lucide-react";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const STEPS = {
  ios: { icon: Share, title: "Install on iPhone / iPad", steps: ["Open this page in Safari.", "Tap the Share button (square with an arrow).", "Scroll and tap “Add to Home Screen”.", "Tap “Add” — FHAPAS appears on your home screen."] },
  android: { icon: MoreVertical, title: "Install on Android", steps: ["Open this page in Chrome (or Edge / Samsung Internet).", "Tap the ⋮ menu in the top-right corner.", "Tap “Install app” or “Add to Home screen”.", "Confirm “Install” — FHAPAS opens like a native app."] },
  desktop: { icon: MonitorDown, title: "Install on desktop", steps: ["Open this page in Chrome or Microsoft Edge.", "Click the install icon at the right of the address bar (or menu ⋮ → “Install FHAPAS”).", "Click “Install” — FHAPAS gets its own window and desktop shortcut.", "On Safari (macOS 14+): File → “Add to Dock”."] },
};

export function InstallHelpDialog({ open, onOpenChange, platform, installed }) {
  const s = STEPS[platform] || STEPS.desktop;
  const Icon = s.icon;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="install-help-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display"><Icon className="h-5 w-5 text-teal-700" /> {installed ? "FHAPAS is installed" : s.title}</DialogTitle>
          <DialogDescription>{installed ? "You are already using the installed app." : "Your browser needs one quick step to add FHAPAS to this device."}</DialogDescription>
        </DialogHeader>
        {installed ? <CheckCircle2 className="mx-auto h-12 w-12 text-teal-600" /> : (
          <ol className="space-y-2.5" data-testid="install-help-steps">
            {s.steps.map((t, i) => (
              <li key={t} className="flex gap-3 text-sm text-slate-700">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-teal-700 text-xs font-bold text-white">{i + 1}</span>{t}
              </li>
            ))}
          </ol>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Clickable hero pill: one-tap install when supported, otherwise guided steps.
export function InstallPill() {
  const { install, installed, platform, helpOpen, setHelpOpen } = usePWAInstall();
  return (
    <>
      <button type="button" onClick={() => (installed ? setHelpOpen(true) : install())} data-testid="hero-install-pill"
        className="inline-flex items-center gap-2 rounded-full border border-teal-200 bg-teal-50 px-3 py-1 text-xs font-semibold text-teal-700 transition-colors hover:bg-teal-100 hover:border-teal-300">
        <Smartphone className="h-3.5 w-3.5" /> Installable PWA · Desktop & Mobile <Download className="h-3.5 w-3.5" />
      </button>
      <InstallHelpDialog open={helpOpen} onOpenChange={setHelpOpen} platform={platform} installed={installed} />
    </>
  );
}

export function PWAInstallBanner() {
  const { canInstall, installed, dismissed, promptInstall, dismiss, isIOS } = usePWAInstall();
  const show = !installed && !dismissed && (canInstall || isIOS);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 30 }}
          className="fixed inset-x-3 bottom-3 z-[60] mx-auto max-w-md safe-bottom sm:left-auto sm:right-4"
          data-testid="pwa-install-banner"
        >
          <div className="flex items-center gap-3 rounded-2xl border border-teal-200 bg-white p-3 shadow-xl">
            <img src="/icon-192.png" alt="FHAPAS" className="h-11 w-11 rounded-xl" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-slate-900">Install FHAPAS</p>
              <p className="truncate text-xs text-slate-500">
                {isIOS ? "Tap Share then “Add to Home Screen”" : "Add to your desktop & mobile"}
              </p>
            </div>
            {!isIOS && (
              <Button
                size="sm"
                data-testid="pwa-install-button"
                className="gap-1.5 bg-teal-700 hover:bg-teal-800"
                onClick={promptInstall}
              >
                <Download className="h-4 w-4" /> Install
              </Button>
            )}
            {isIOS && <Share className="h-5 w-5 text-teal-600" />}
            <button data-testid="pwa-dismiss-button" onClick={dismiss} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100">
              <X className="h-4 w-4" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function InstallButton({ className = "" }) {
  const { installed, install, platform, helpOpen, setHelpOpen } = usePWAInstall();
  if (installed) return null;
  return (
    <>
      <Button
        variant="outline"
        size="sm"
        data-testid="header-install-button"
        onClick={install}
        className={`gap-1.5 border-teal-200 text-teal-700 hover:bg-teal-50 ${className}`}
      >
        <Download className="h-4 w-4" /> Install App
      </Button>
      <InstallHelpDialog open={helpOpen} onOpenChange={setHelpOpen} platform={platform} installed={installed} />
    </>
  );
}
